/* eslint-disable no-unused-vars */

/**
 * @module OIDCProviderAdapter
 * @description Abstract base class for OIDC provider adapters.
 * All OIDC providers (Auth0, custom OIDC, etc.) must implement this interface.
 */

/**
 * @typedef {object} OIDCProviderConfig
 * @property {string} [domain] - The OIDC provider domain (Auth0-specific)
 * @property {string} [issuerUrl] - The OIDC issuer URL (custom OIDC)
 * @property {string} clientId - The client ID for the OIDC application
 * @property {string} [redirectUri] - The redirect URI after authentication
 * @property {string} [audience] - The API audience (Auth0-specific)
 * @property {string} [connection] - The connection name (Auth0-specific)
 * @property {object} [authorizationParams] - Additional authorization parameters
 */

/**
 * @typedef {object} LoginOptions
 * @property {object} [authorizationParams] - Parameters to pass to the authorization endpoint
 * @property {object} [appState] - Application state to preserve through the auth flow
 */

/**
 * @typedef {object} TokenResponse
 * @property {string} access_token - The access token
 * @property {string} [id_token] - The ID token
 * @property {string} [refresh_token] - The refresh token
 * @property {number} [expires_in] - Token expiration time in seconds
 * @property {string} [token_type] - Token type (typically "Bearer")
 */

/**
 * @typedef {object} UserProfile
 * @property {string} sub - The user's subject identifier
 * @property {string} [email] - The user's email
 * @property {string} [name] - The user's full name
 * @property {string} [picture] - URL to the user's profile picture
 */

/**
 * Abstract base class for OIDC provider adapters.
 * Subclasses must implement all methods.
 */
class OIDCProviderAdapter {
  /**
   * @param {OIDCProviderConfig} config - Configuration for the OIDC provider
   */
  constructor(config) {
    if (new.target === OIDCProviderAdapter) {
      throw new Error('OIDCProviderAdapter is an abstract class and cannot be instantiated directly');
    }
    this.config = config;
  }

  /**
   * Initialize the OIDC client.
   * @returns {Promise<this>} The initialized adapter instance
   */
  async createClient() {
    throw new Error('createClient() must be implemented by subclass');
  }

  /**
   * Initiate the login flow with redirect.
   * @param {LoginOptions} options - Login options
   * @returns {Promise<void>}
   */
  async loginWithRedirect(options) {
    throw new Error('loginWithRedirect() must be implemented by subclass');
  }

  /**
   * Handle the callback after login redirect.
   * @returns {Promise<{appState: Object}>} The app state preserved through the auth flow
   */
  async handleRedirectCallback() {
    throw new Error('handleRedirectCallback() must be implemented by subclass');
  }

  /**
   * Check if the user is authenticated.
   * @returns {Promise<boolean>} True if authenticated
   */
  async isAuthenticated() {
    throw new Error('isAuthenticated() must be implemented by subclass');
  }

  /**
   * Get an access token.
   * @param {Object} [options] - Token options
   * @returns {Promise<string>} The access token
   */
  async getTokenSilently(options) {
    throw new Error('getToken() must be implemented by subclass');
  }

  /**
   * Get the user profile.
   * @returns {Promise<UserProfile|null>} The user profile or null if not authenticated
   */
  async getUser() {
    throw new Error('getUser() must be implemented by subclass');
  }

  /**
   * Logout the user.
   * @param {Object} [options] - Logout options
   * @returns {Promise<void>}
   */
  async logout(options) {
    throw new Error('logout() must be implemented by subclass');
  }

  /**
   * Get the provider type identifier.
   * @returns {string} The provider type (e.g., 'auth0', 'ssas')
   */
  getProviderType() {
    throw new Error('getProviderType() must be implemented by subclass');
  }

  /**
   * Cleanup any locally stored auth state such as cookies or local storage
   */
  cleanAuthState() {
    throw new Error('cleanAuthState() must be implemented by subclass');
  }

  /**
   * Enrich a user profile with additional IDP-specific data.
   * @param {UserProfile} userProfile - The base user profile
   * @returns {Promise<UserProfile>} The enriched user profile
   */
  async enrichUserProfile(userProfile) {
    throw new Error('enrichUserProfile() must be implemented by subclass');
  }

  async devUnlinkAllSsoAccounts() {
    throw new Error('devUnlinkAllSsoAccounts() must be implemented by subclass');
  }
}

export { OIDCProviderAdapter };
